import { _ } from '/@/locale';
import type { RecommendationVariant } from '/@/views/recommendations/use-save-recommendation';

/**
 * Which of the result page's titles applies.
 *
 * `management-named` is the one the design asks for; `management` is the fallback for the window
 * where the hybrid's name is still loading, since the named title reads as a fragment without it.
 */
export type ResultTitleKind = 'hybrid' | 'management' | 'management-named';

/**
 * Keyed on the wizard that produced the recommendation, not on how many hybrids it holds: a
 * hybrid recommendation narrowed down to one hybrid is still a hybrid recommendation.
 */
export const getResultTitleKind = ({
    variant,
    hybridName,
}: {
    variant: RecommendationVariant;
    hybridName?: string;
}): ResultTitleKind => {
    if (variant !== 'management') return 'hybrid';
    return hybridName ? 'management-named' : 'management';
};

/**
 * Shared by the result page and the PDF so a recommendation is named the same in both — the
 * PDF is what gets shared onward, and a document titled differently from the page it came from
 * reads as a different recommendation.
 */
export const getResultTitle = (args: {
    variant: RecommendationVariant;
    hybridName?: string;
    /** The crop's display name, e.g. "maíz". */
    cropName?: string;
}): string => {
    switch (getResultTitleKind(args)) {
        case 'management-named':
            return _('Recomendación de manejo de {{hybrid}}', { hybrid: args.hybridName });
        case 'management':
            return _('Recomendación de manejo');
        default:
            return _('Recomendación de híbrido de {{crop}}', { crop: args.cropName });
    }
};

/**
 * Whether to render the single-hybrid layout — the hybrid's card beside the density chart,
 * instead of a separate "Híbridos recomendados" section above it.
 *
 * Deliberately a count and not the variant: a hybrid recommendation the user narrowed to one
 * hybrid has the same empty comparison as a management recommendation, and should read the
 * same way.
 */
export const isSingleHybridLayout = (hybridCount: number): boolean => hybridCount === 1;
